/**
 * Offline Banner Component
 * Displays a visual indicator when the app is offline
 * Shows pending sync count and manual sync option
 */

import { useTranslation } from 'react-i18next';
import { WifiOff, CloudOff, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { useNetwork } from '@/contexts/NetworkContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { SyncConflictDialog } from '@/components/desktop/SyncConflictDialog';

interface OfflineBannerProps {
  className?: string;
  showWhenOnline?: boolean;
}

export function OfflineBanner({ className, showWhenOnline = false }: OfflineBannerProps) {
  const { t } = useTranslation();
  const { 
    isOnline, 
    isElectronApp, 
    pendingSyncCount, 
    forceSync, 
    connectionQuality,
    conflicts,
    hasConflicts,
    resolveConflict,
    resolveAllConflicts,
  } = useNetwork();
  const [isSyncing, setIsSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);

  // Handle manual sync
  const handleSync = async () => {
    if (!forceSync || isSyncing) return;
    
    setIsSyncing(true);
    setJustSynced(false);
    
    try {
      await forceSync();
      setJustSynced(true);
      // Reset the "just synced" indicator after 3 seconds
      setTimeout(() => setJustSynced(false), 3000);
    } catch (err) {
      console.error('[OfflineBanner] Sync failed:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  // Don't show anything if online and showWhenOnline is false (unless there are conflicts)
  if (isOnline && !showWhenOnline && !hasConflicts) {
    return null;
  }

  // Conflict alert banner (shown when online with conflicts)
  if (isOnline && hasConflicts) {
    return (
      <>
        <div
          className={cn(
            'flex items-center justify-between gap-2 px-4 py-2 text-sm',
            'bg-orange-50 dark:bg-orange-900/30 border-b border-orange-200 dark:border-orange-800',
            className
          )}
        >
          <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">
              {t('offline.conflict.title', 'Sync Conflicts Detected')}
            </span>
            <span className="text-orange-600 dark:text-orange-400">
              ({conflicts.length})
            </span>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConflictDialogOpen(true)}
            className="h-7 px-2 text-orange-700 hover:text-orange-800 hover:bg-orange-100 dark:text-orange-300"
          >
            Resolve
          </Button>
        </div>

        <SyncConflictDialog
          open={conflictDialogOpen}
          onOpenChange={setConflictDialogOpen}
          conflicts={conflicts}
          onResolve={resolveConflict}
          onResolveAll={resolveAllConflicts}
        />
      </>
    );
  }

  // Online status (when showWhenOnline is true)
  if (isOnline && showWhenOnline) {
    // Only show for Electron with pending syncs
    if (!isElectronApp || pendingSyncCount === 0) {
      return null;
    }

    return (
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-4 py-2 text-sm',
          'bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800',
          className
        )}
      >
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <CloudOff className="h-4 w-4" />
          <span>
            {t('offline.pendingSync', {
              count: pendingSyncCount,
              defaultValue: `${pendingSyncCount} changes pending sync`,
            })}
          </span>
        </div>
        
        {forceSync && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing}
            className="h-7 px-2 text-amber-700 hover:text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:text-amber-200 dark:hover:bg-amber-800"
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                {t('offline.syncing', 'Syncing...')}
              </>
            ) : justSynced ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                {t('offline.synced', 'Synced!')}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('offline.syncNow', 'Sync Now')}
              </>
            )}
          </Button>
        )}
      </div>
    );
  }

  // Offline status
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 px-4 py-2 text-sm',
        'bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800',
        className
      )}
    >
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
        <WifiOff className="h-4 w-4" />
        <span className="font-medium">
          {t('offline.youAreOffline', 'You are offline')}
        </span>
        {isElectronApp && (
          <span className="text-red-600 dark:text-red-400">
            {t('offline.desktopMode', '— Desktop mode active')}
          </span>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {pendingSyncCount > 0 && (
          <span className="text-red-600 dark:text-red-400 text-xs">
            {t('offline.pendingChanges', {
              count: pendingSyncCount,
              defaultValue: `${pendingSyncCount} pending`,
            })}
          </span>
        )}
        
        {connectionQuality && connectionQuality === 'poor' && (
          <span className="text-xs text-red-500 dark:text-red-400">
            {t('offline.poorConnection', 'Poor connection')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Compact offline indicator for headers/sidebars
 */
export function OfflineIndicator({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { isOnline, pendingSyncCount } = useNetwork();

  if (isOnline && pendingSyncCount === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium',
        isOnline
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
          : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
        className
      )}
      title={isOnline 
        ? t('offline.pendingSync', { count: pendingSyncCount, defaultValue: `${pendingSyncCount} pending sync` })
        : t('offline.youAreOffline', 'You are offline')
      }
    >
      {isOnline ? (
        <>
          <CloudOff className="h-3 w-3" />
          <span>{pendingSyncCount}</span>
        </>
      ) : (
        <>
          <WifiOff className="h-3 w-3" />
          <span>{t('offline.offline', 'Offline')}</span>
        </>
      )}
    </div>
  );
}
